import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SupportAgentOutlined } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { DroppableTicketList } from 'components/ui/DroppableTicketList';
import { PageHeader } from 'components/ui/Global/PageHeader';
import { SingleTicketView } from 'components/ui/tickets/SingleTicketView';
import { useAppState } from 'context/AppContext';
import qtyUpdate from 'data/atoms/qtyUpdate';
import userAtom from 'data/atoms/userAtom';
import dayjs from 'dayjs';
import { getFilteredList } from 'functions/misc';
import { rq, rqPost } from 'http/apiRoutes';
import { useGet, usePut } from 'http/useApi';
import { t } from 'i18next';
import { ITicketViewModel } from 'Interfaces/ITicketConfig';
import { useAtom } from 'jotai';
import React, { FC, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * TicketsList displays draggable lists of tickets: new and assigned to user.
 */
export const TicketsList: FC = () => {
  const [filterType, setFilterType] = useState<number>(1);
  const { user } = useAppState(); // ✅ global user

  console.log('ttt user in TicketsList', user);
  // Ticket states
  const [activeTicket, setActiveTicket] = useState<ITicketViewModel | null>(
    null
  );
  const [activeTicketId, setActiveTicketId] = useState<string | null>(null);
  const [sortedTickets, setSortedTickets] = useState<ITicketViewModel[]>([]);
  const [sortedUserTickets, setSortedUserTickets] = useState<
    ITicketViewModel[]
  >([]);

  // ✅ Bruk state, ikke ref, så query key faktisk endres
  const [boardNr, setBoardNr] = useState<number>(1);

  const columns: Array<'newTickets' | 'userTickets'> = [
    'newTickets',
    'userTickets',
  ];

  // API

  const [isQtyUpdate, setIsQtyUpdate] = useAtom(qtyUpdate);

  // ✅ Stabil enabled – fyrer så fort employee har id
  const isEnabled = !!user?.id;
  console.log('ttt isEnabled', isEnabled, user);

  const { data: userTicketsData } = useGet(
    rq.userTickets(1, user?.id ?? '', isEnabled),
  );

  const { data: newTicketsData } = useGet(
    rq.tickets(boardNr, 0, isEnabled),
  );

  const isPutEnabled = !!activeTicketId;
  const { mutate: putTicket } = usePut(
    rqPost.ticketResponsible(activeTicketId ?? '', isPutEnabled),
  );

  // Sync API data
  useEffect(() => {
    if (newTicketsData?.tickets) {
      console.log('ttt newTicketsData', newTicketsData);

      const sorted = [...newTicketsData.tickets].sort((a, b) =>
        dayjs(b.updated).isAfter(dayjs(a.updated)) ? 1 : -1
      );
      setSortedTickets(sorted);
    }
  }, [newTicketsData]);

  useEffect(() => {
    console.log('ttt userTicketsData.tickets', userTicketsData);

    if (userTicketsData?.tickets) {
      const sorted = [...userTicketsData.tickets].sort((a, b) =>
        dayjs(b.updated).isAfter(dayjs(a.updated)) ? 1 : -1
      );
      setSortedUserTickets(sorted);
    }
  }, [userTicketsData]);

  // Handlers
  // ✅ Nå trigges rerender + refetch
  const handleFilterChange = (value: number) => {
    setFilterType(value);
    setBoardNr(value);
  };

  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleSearch = (
    e: React.ChangeEvent<HTMLInputElement>,
    col: 'userTickets' | 'newTickets'
  ) => {
    const value = e.target.value;
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      if (col === 'userTickets') {
        setSortedUserTickets(
          getFilteredList(userTicketsData?.tickets ?? [], value)
        );
        return;
      }
      if (col === 'newTickets') {
        setSortedTickets(getFilteredList(newTicketsData?.tickets ?? [], value));
        return;
      }
    }, 300); // 300ms debounce
  };

  const handleAssign = (ticket: ITicketViewModel) => {
    const payload = { ticketId: ticket.id, responsibleId: user.id };
    putTicket(payload);

    // Optimistically update
    setSortedTickets((prev) => prev.filter((t) => t.id !== ticket.id));
    setSortedUserTickets((prev) => [ticket, ...prev]);
    setIsQtyUpdate(true);
  };

  const handleUnassign = (ticket: ITicketViewModel) => {
    const payload = { ticketId: ticket.id, responsibleId: null };
    putTicket(payload);

    setSortedUserTickets((prev) => prev.filter((t) => t.id !== ticket.id));
    setSortedTickets((prev) => [ticket, ...prev]);
    setIsQtyUpdate(true);
  };

  // DnD
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } })
  );

  const handleDragStart = (e: any) => {
    setActiveTicketId(e.active.id);
    setActiveTicket(e.active.data.current.ticket as ITicketViewModel);
  };

  const handleDragEnd = (e: any) => {
    const { active, over } = e;
    setActiveTicketId(null);
    setActiveTicket(null);

    const getColumn = (id: string) =>
      sortedTickets.some((t) => t.id === id)
        ? 'newTickets'
        : sortedUserTickets.some((t) => t.id === id)
        ? 'userTickets'
        : null;

    const activeCol = getColumn(active.id);
    const overCol = over?.id as string | null;
    if (activeCol === overCol || !overCol) return;

    const movedTicket =
      activeCol === 'newTickets'
        ? sortedTickets.find((t) => t.id === active.id)
        : sortedUserTickets.find((t) => t.id === active.id);
    if (!movedTicket) return;

    if (activeCol === 'newTickets' && overCol === 'userTickets') {
      handleAssign(movedTicket);
    }
    if (activeCol === 'userTickets' && overCol === 'newTickets') {
      handleUnassign(movedTicket);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <PageHeader
        Icon={SupportAgentOutlined}
        title={t('ticket.header')}
        HeaderButtons={[]}
        FormJSX={null}
      />

      <Grid
        container
        sx={{ display: 'flex', flexDirection: 'row', flexGrow: 1 }}
      >
        {columns.map((col) => (
          <Grid
            item
            xs={6}
            key={col}
            sx={{
              pr: col === 'newTickets' ? 0.5 : 0,
              pl: col === 'userTickets' ? 0.5 : 0,
            }}
          >
            <DroppableTicketList
              id={col}
              tickets={col === 'newTickets' ? sortedTickets : sortedUserTickets}
              setActiveTicket={setActiveTicket}
              handleFilterChange={handleFilterChange}
              filterType={filterType}
              handleSearch={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleSearch(e, col)
              }
            />
          </Grid>
        ))}
      </Grid>

      {createPortal(
        <DragOverlay>
          {activeTicket && <SingleTicketView ticket={activeTicket} />}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
};
